import { isNotBlank } from '~/lib/blank/blank'
import { useIsPC } from '~/lib/ua'
import { sva } from '~/styled-system/css'
import {
  useDisplayCancelPolicies,
  useDisplayDecrementVisitorPolicies,
} from '../../store/cancelPolicy'
import type { DisplayCancelPolicy } from '../../store/cancelPolicy'
import { usePaymentMethod } from '../../store/stateMachine/context'
import { useCancelPolicy } from '../../store/stateMachine/state'

const cancelPolicyBodyStyles = sva({
  slots: ['container', 'body', 'title', 'text'],
  base: {
    container: {
      spaceY: '3',
      py: '2',
      _pc: {
        fontSize: 'md',
        spaceY: '4',
      },
    },
    body: {
      spaceY: '2',
      _pc: {
        fontSize: 'md',
      },
    },
    title: {
      fontSize: 'md',
    },
    text: {
      whiteSpace: 'pre-wrap',
    },
  },
})

export function CancelPolicyBody() {
  const { cancelPolicy, policies, decrementVisitorPolicies } = useCancelPolicyBody()
  const classes = cancelPolicyBodyStyles()
  if (cancelPolicy === undefined) {
    return <p>なし</p>
  }
  if (cancelPolicy.__typename === 'CancelPolicyText') {
    return <p className={classes.text}>{cancelPolicy.text}</p>
  }
  return (
    <div className={classes.container}>
      <p>当予約のキャンセル・変更の場合、以下のキャンセル料を申し受けます。</p>
      <Detail policies={policies} label="■ 予約取消時" />
      <Detail policies={decrementVisitorPolicies} label="■ 予約変更時" />
      <Note note={cancelPolicy.note} />
    </div>
  )
}

function useCancelPolicyBody() {
  const cancelPolicy = useCancelPolicy()
  const policies = useDisplayCancelPolicies()
  const decrementVisitorPolicies = useDisplayDecrementVisitorPolicies()
  return { cancelPolicy, policies, decrementVisitorPolicies }
}

function Detail({ policies, label }: { policies: DisplayCancelPolicy[]; label: string }) {
  const isPC = useIsPC()
  if (policies.length === 0) {
    return null
  }
  const Table = isPC ? PcTable : SpTable
  const classes = cancelPolicyBodyStyles()
  return (
    <div className={classes.body}>
      <p className={classes.title}>{label}</p>
      <Table policies={policies} />
    </div>
  )
}

const spTableStyles = sva({
  slots: ['table', 'condition', 'penalty'],
  base: {
    table: {
      w: 'full',
      tableLayout: 'auto',
    },
    condition: {
      border: 'medium',
      bg: 'gray.100',
      p: '2',
    },
    penalty: {
      border: 'medium',
      p: '2',
    },
  },
})

function SpTable({ policies }: { policies: DisplayCancelPolicy[] }) {
  const classes = spTableStyles()
  return (
    <table className={classes.table}>
      <tbody>
        {policies.map((p, i) => {
          return (
            <tr key={i}>
              <td className={classes.condition}>{p.condition}</td>
              <td className={classes.penalty}>{p.penalty}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

const pcTableStyles = sva({
  slots: ['container', 'list', 'condition', 'penalty'],
  base: {
    container: {
      display: 'inline-flex',
      border: 'medium',
    },
    list: {
      textAlign: 'center',
      borderLeft: 'medium',
      p: '2',
      spaceY: '1',
      fontSize: 'sm',
      minWidth: '[100px]',
      _first: {
        border: 'none',
      },
    },
  },
})

function PcTable({ policies }: { policies: DisplayCancelPolicy[] }) {
  const classes = pcTableStyles()
  return (
    <div className={classes.container}>
      {policies.map((p, i) => {
        return (
          <dl key={i} className={classes.list}>
            <dt>{p.condition}</dt>
            <dd>{p.penalty}</dd>
          </dl>
        )
      })}
    </div>
  )
}

function Note({ note }: { note: string }) {
  const paymentMethod = usePaymentMethod()
  return (
    <>
      {isNotBlank(note) && <p>※{note}</p>}
      {paymentMethod === 'ONLINE' && (
        <p>※キャンセル料は、ご入力いただいたクレジットカードでのお支払いとなります。</p>
      )}
    </>
  )
}
